import { VerifiedClient } from '../../../types';

const isPastTargetDate = () => {
    const currentYear = new Date().getFullYear();
    return currentYear > 2024;
};

const loadJSPDF = (): Promise<any> => {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
        script.onload = () => {
            const jsPDF = (window as any).jspdf.jsPDF;
            if (jsPDF) {
                resolve(jsPDF);
            } else {
                reject(new Error("Failed to load jsPDF"));
            }
        };
        script.onerror = () => reject(new Error(`Failed to load script: ${script.src}`));
        document.body.appendChild(script);
    });
};

export const renderDownloadPDF = async (verifiedClients: VerifiedClient[], province: string, fileNumber: string) => {
    const jsPDF = await loadJSPDF();
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const config = {
        margin: 20,
        imageWidth: 80,
        imageHeight: 60,
        scoresYOffset: 6,
        pageEndYOffset: 40,
        scoresPositionYOffset: 17,
        pageHeight: doc.internal.pageSize.height,
        pageWidth: doc.internal.pageSize.width
    }
    let yOffset = config.margin;

    doc.setFontSize(20);
    doc.setFont(undefined, 'bold')
    doc.text(`Client ID Details for File: ${fileNumber}`, config.margin, yOffset).setFont(undefined, "normal");
    yOffset += 20;

    const addClientDetails = (client) => {
        doc.setFontSize(14);

        doc.setFont(undefined, 'bold');
        doc.text('Name:', config.margin, yOffset);
        const nameWidth = doc.getTextWidth('Name: ');
        doc.setFont(undefined, 'normal');
        const maxWidth = (province !== 'AB' ? pageWidth/2 : (isPastTargetDate() ? pageWidth/2 : pageWidth)) - config.margin * 2 - nameWidth;
        const wrappedName = doc.splitTextToSize(client.clientInformation.Name, maxWidth);
        doc.text(wrappedName, config.margin + nameWidth, yOffset);
        yOffset += (wrappedName.length+1) * 5;

        doc.setFont(undefined, 'bold');
        doc.text('ID Type:', config.margin, yOffset);
        const idTypeWidth = doc.getTextWidth('ID Type: ');
        doc.setFont(undefined, 'normal');
        doc.text(client.clientInformation.Type, config.margin + idTypeWidth, yOffset);
        yOffset += 10;

        doc.setFont(undefined, 'bold');
        doc.text('Face Verification Passed:', config.margin, yOffset);
        const faceVerificationResultWidth = doc.getTextWidth('Face Verification Passed: ');
        doc.setFont(undefined, 'normal');
        doc.text(client.FaceVerificationPassed ? 'Yes' : 'No', config.margin + faceVerificationResultWidth, yOffset);
        yOffset += 10;

        const addLabeledValue = (doc: any, label: string, value: string, x: number, y: number) => {
            doc.setFont(undefined, 'bold');
            doc.text(label, x, y);
            const labelWidth = doc.getTextWidth(label);
            doc.setFont(undefined, 'normal');
            doc.text(value, x + labelWidth, y);
            return doc.getTextDimensions(value).h;
        };
        if ((isPastTargetDate() && province === "AB") || (province !== 'AB')) {
            yOffset -= (config.scoresPositionYOffset + (wrappedName.length * 5));
            const baseX = config.margin + config.imageWidth + 10;
        
            const labels = [
                { text: 'Certainty: ', value: client.clientIdFraudCheck.certainty === 'NotPerformed' ? 'Low' : client.clientIdFraudCheck.certainty },
                { text: 'Score: ', value: client.clientIdFraudCheck.score },
                { text: 'Failed Checks: ', value: client.clientIdFraudCheck.failedChecks },
                { text: 'Checks not performed: ', value: client.clientIdFraudCheck.notPerformedChecks },
                { text: 'Passed Checks: ', value: client.clientIdFraudCheck.passedChecks },
                { text: 'Total Checks: ', value: client.clientIdFraudCheck.totalChecks }
            ];
        
            labels.forEach(item => {
                const textHeight = addLabeledValue(doc, item.text, item.value.toString(), baseX, yOffset);
                yOffset += Math.max(textHeight, config.scoresYOffset);
            });
        }

        const frontImage = client.clientIDImages.frontImage;
        const backImage = client.clientIDImages.backImage;

        if (frontImage || backImage) {
            if (yOffset + config.imageHeight > config.pageHeight) {
                doc.addPage();
                yOffset = config.margin;
            }

            if (frontImage) {
                doc.addImage(frontImage, 'PNG', config.margin, yOffset, config.imageWidth, config.imageHeight);
            } else {
                doc.text('Front Image Not Available', config.margin, yOffset + config.imageHeight / 2);
            }

            if (backImage) {
                doc.addImage(backImage, 'PNG', config.margin + config.imageWidth + 10, yOffset, config.imageWidth, config.imageHeight);
            } else {
                doc.text('Back Image Not Available', config.margin + config.imageWidth + 10, yOffset + config.imageHeight / 2);
            }

            yOffset += config.imageHeight + 10;
        } else {
            yOffset += 10;
        }

        doc.setDrawColor(0);
        doc.setLineWidth(0.5); 
        doc.line(config.margin, yOffset, pageWidth - config.margin, yOffset); 
        yOffset += 10; 
    };

    verifiedClients.forEach((client) => {
        if (yOffset + config.pageEndYOffset > config.pageHeight) {
            doc.addPage();
            yOffset = config.margin;
        }
        addClientDetails(client);
    });

    doc.save(`Client_ID_Details_${fileNumber}.pdf`);
};